<template>
  <div class="cost-category-view">
    <div class="page-header">
      <h2>원가 카테고리 관리</h2>
      <div class="header-actions">
        <el-button
          v-if="userStore.isCostManager"
          type="primary"
          @click="openCreateDialog"
        >
          <el-icon><Plus /></el-icon>
          신규 카테고리
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <el-card>
      <el-tree
        :data="treeData || []"
        :props="treeProps"
        node-key="id"
        default-expand-all
        v-loading="loading"
      >
        <template #default="{ data }">
          <div class="tree-node">
            <span class="node-label">
              <el-tag size="small" :type="getCategoryType(data.category_type)">
                {{ data.category_type_display || data.category_type }}
              </el-tag>
              <strong>{{ data.code }}</strong>
              {{ data.name }}
            </span>
            <span class="node-actions" v-if="userStore.isCostManager">
              <el-button size="small" link @click.stop="openEditDialog(data)">수정</el-button>
              <el-button size="small" link type="primary" @click.stop="openAddChildDialog(data)">
                하위 추가
              </el-button>
            </span>
          </div>
        </template>
      </el-tree>
    </el-card>

    <!-- 생성/수정 다이얼로그 -->
    <el-dialog
      v-model="showDialog"
      :title="isEdit ? '카테고리 수정' : '신규 카테고리'"
      width="500px"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="코드" prop="code">
          <el-input v-model="form.code" placeholder="예: MAT-001" />
        </el-form-item>
        <el-form-item label="이름" prop="name">
          <el-input v-model="form.name" placeholder="카테고리명" />
        </el-form-item>
        <el-form-item label="유형" prop="category_type">
          <el-select v-model="form.category_type" style="width: 100%">
            <el-option label="자재비" value="MATERIAL" />
            <el-option label="인건비" value="LABOR" />
            <el-option label="경비" value="OVERHEAD" />
            <el-option label="외주비" value="OUTSOURCING" />
            <el-option label="물류비" value="LOGISTICS" />
            <el-option label="기타" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="상위 카테고리">
          <el-cascader
            v-model="form.parent_path"
            :options="flatCategories"
            :props="{ checkStrictly: true, value: 'id', label: 'name' }"
            clearable
            placeholder="최상위"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="정렬순서">
          <el-input-number v-model="form.sort_order" :min="0" :max="999" />
        </el-form-item>
        <el-form-item label="활성화">
          <el-switch v-model="form.is_active" />
        </el-form-item>
        <el-form-item label="설명">
          <el-input v-model="form.description" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showDialog = false">취소</el-button>
        <el-button type="primary" @click="handleSubmit">저장</el-button>
      </template>
    </el-dialog>

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/cost-category'"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { Plus, QuestionFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import HelpPanel from '@/components/help/HelpPanel.vue'

const userStore = useUserStore()

const loading = ref(false)
const treeData = ref([])
const flatCategories = ref([])
const helpVisible = ref(false)
const showDialog = ref(false)
const isEdit = ref(false)
const formRef = ref(null)

const form = reactive({
  id: null,
  code: '',
  name: '',
  category_type: 'MATERIAL',
  parent: null,
  parent_path: [],
  sort_order: 0,
  is_active: true,
  description: ''
})

const rules = {
  code: [{ required: true, message: '코드를 입력하세요', trigger: 'blur' }],
  name: [{ required: true, message: '이름을 입력하세요', trigger: 'blur' }],
  category_type: [{ required: true, message: '유형을 선택하세요', trigger: 'change' }]
}

const treeProps = {
  children: 'children',
  label: 'name'
}

const getCategoryType = (type) => {
  const types = {
    'MATERIAL': '',
    'LABOR': 'success',
    'OVERHEAD': 'warning',
    'OUTSOURCING': 'info',
    'LOGISTICS': 'danger',
    'OTHER': 'info'
  }
  return types[type] || 'info'
}

const fetchData = async () => {
  loading.value = true
  try {
    const response = await axiosInstance.get('cost-categories/tree/')
    treeData.value = response.data

    // flat 목록도 가져오기
    const flatResponse = await axiosInstance.get('cost-categories/')
    flatCategories.value = flatResponse.data.results || flatResponse.data
  } catch (error) {
    console.error('Failed to fetch categories:', error)
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  form.id = null
  form.code = ''
  form.name = ''
  form.category_type = 'MATERIAL'
  form.parent = null
  form.parent_path = []
  form.sort_order = 0
  form.is_active = true
  form.description = ''
}

const openCreateDialog = () => {
  resetForm()
  isEdit.value = false
  showDialog.value = true
}

const openEditDialog = (data) => {
  isEdit.value = true
  form.id = data.id
  form.code = data.code
  form.name = data.name
  form.category_type = data.category_type
  form.parent = data.parent
  form.parent_path = data.parent ? [data.parent] : []
  form.sort_order = data.sort_order
  form.is_active = data.is_active
  form.description = data.description || ''
  showDialog.value = true
}

const openAddChildDialog = (parentData) => {
  resetForm()
  isEdit.value = false
  form.parent = parentData.id
  form.parent_path = [parentData.id]
  form.category_type = parentData.category_type
  showDialog.value = true
}

const handleSubmit = async () => {
  await formRef.value.validate()

  const data = {
    code: form.code,
    name: form.name,
    category_type: form.category_type,
    parent: form.parent_path.length > 0 ? form.parent_path[form.parent_path.length - 1] : null,
    sort_order: form.sort_order,
    is_active: form.is_active,
    description: form.description
  }

  try {
    if (isEdit.value) {
      await axiosInstance.put(`cost-categories/${form.id}/`, data)
      ElMessage.success('카테고리가 수정되었습니다.')
    } else {
      await axiosInstance.post('cost-categories/', data)
      ElMessage.success('카테고리가 생성되었습니다.')
    }
    showDialog.value = false
    fetchData()
  } catch (error) {
    ElMessage.error('저장에 실패했습니다.')
    console.error(error)
  }
}

const showHelp = () => {
  helpVisible.value = true
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.cost-category-view {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.tree-node {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 10px;
}

.node-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.node-actions {
  display: none;
}

.el-tree-node:hover .node-actions {
  display: inline-block;
}
</style>
